<template>
  <div
    class="card"
    :class="classes"
  >
    <div
      class="card__name body-2"
      v-html="$t(`products.${productId}.name`)"
    />

    <div
      class="card__designation h3"
      :data-product-id="productId"
      v-html="$t(`products.${productId}.designation`)"
    />

    <div class="card__pricing">
      <basic-product-pricing
        v-if="localProduct?.prices?.length > 0"
        :product="localProduct"
      />
    </div>

    <div
      class="card__description body-2"
      v-html="$t(`products.${productId}.description`)"
    />

    <div class="card__criteria">
      <product-options
        v-if="categoryId && groupId && person"
        :category-id="categoryId"
        :group-id="groupId"
        :mode="isProductEditable ? 'write' : 'read'"
        :person="person"
        :product="localProduct"
        :source="$props.source"
        @change="selectOption"
      />
    </div>

    <div class="card__message">
      <template
        v-for="(message, index) in messages"
        :key="`message-${index}`"
      >
        <basic-message
          :actions="message.actions"
          :closable="message.closeable"
          :content="message.content"
          :severity="message.severity"
        />
      </template>
    </div>

    <div class="card__price">
      <basic-product-price
        v-if="localProduct?.prices?.length > 0"
        :product="localProduct"
        :prefix="pricePrefix"
        :suffix="$t('product.currency')"
        price-class="lead-1"
        suffix-class="body-1"
      >
        <span
          v-if="['OHAMProdukt', 'ANETProdukt'].includes(localProduct.productId) && !localProduct.doctor"
          v-text="'*'"
        />
      </basic-product-price>
    </div>

    <div class="card__action">
      <product-action
        v-if="$props.personId"
        :person-id="$props.personId"
        :product="localProduct"
        :product-state="productState"
        :is-dirty="isDirty"
        @add="add"
        @apply="apply"
        @open-detail="productDetailModalRef.modalRef.open()"
        @remove="remove"
        @search="searchMedicalOffice"
      />

      <basic-button
        v-else-if="!$props.personId"
        :label="$t('benefits.widget.productLinkText')"
        @click="$emit('open-form')"
      />
    </div>

    <template v-if="!isReady">
      <div class="card__loader">
        <the-icon
          class="fa-spin"
          art="duo"
          name="loader"
        />
      </div>
    </template>

    <dvp-product-modal
      v-if="categoryId && person"
      :id="`productDetailModal_${$props.productId}`"
      ref="productDetailModalRef"
      :category-id="categoryId"
      :group-id="groupId"
      :modal-title="$t('product.criteriaInformation')"
      :person="person"
      :product="localProduct"
      :source="$props.source"
    />
  </div>
</template>

<script setup>
  import { computed, ref, watch, watchEffect } from 'vue'
  import { cloneDeep } from 'lodash'

  import { events$, loading$ } from '@shared/services'

  import basketStore from '@shared/store/basket'
  import productStructureStore from '@shared/store/productStructure'
  import useI18n from '@shared/hooks/useI18n'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'
  import useProduct from '@shared/hooks/useProduct'
  import useProductPersons from '@shared/hooks/useProductPersons'
  import usePerson from '@shared/hooks/usePerson'
  import useProductState from '@shared/hooks/useProductState'
  import { ReactivityUtil } from '@shared/utils/Reactivity'
  import { isProductReadOnlyHaX } from '@shared/utils/HaX'

  import { BasicButton, BasicMessage, BasicProductPrice, BasicProductPricing } from '@shared/components/Basic'
  import ProductAction from '@shared/components/Product/ProductAction'
  import ProductOptions from '@shared/components/Product/Options'
  import DvpProductModal from '@dvp/components/ProductModal'

  import { BASKET_OFFER_EDIT_SCOPE, PRODUCT_STATE } from '@shared/config/constants'
  import { EVENT_MEDICAL_OFFICE_MODAL_OPEN, EVENT_PRODUCT } from '@shared/config/events'

  // HOOKS
  const { t } = useI18n()
  const { getPerson } = usePerson()
  const { getProductMessages, getSimulatedProductSelection, removeProductFromPerson, updateProductForPerson } =
    usePersonSpecificProduct()
  const { getCategoryIdFromProduct, getGroupIdFromProduct, isUpgrade } = useProduct()
  const { addProductToOvpBasket, addProductWithDoctor } = useProductPersons()
  const { state } = useProductState()

  // INIT
  defineEmits(['open-form'])
  const props = defineProps({
    isNarrow: {
      type: Boolean,
      default: false,
    },

    personId: {
      type: String,
      default: null,
    },

    product: {
      type: Object,
      required: true,
    },

    source: {
      type: String,
      required: true,
    },
  })

  // DATA
  const localProduct = ref(props.product)
  const isDirty = ref(false)
  const isReady = ref(true)
  const productDetailModalRef = ref()

  // COMPUTED
  const categoryId = computed(() => {
    return getCategoryIdFromProduct(props.product.productId)
  })

  const classes = computed(() => {
    return {
      'appearance-extended': props.product.selected,
      'card--selected': props.product.selected,
      'card--wide': !props.isNarrow,
      'product--invalid': !props.product.valid && !!localProduct.value?.messages?.find(m => m.type === 'ERROR'),
      'product--unavailable': !localProduct.value.valid && !!localProduct.value?.messages?.find(m => m.type === 'INFO'),
    }
  })

  const groupId = computed(() => {
    return getGroupIdFromProduct(props.product.productId)
  })

  const hasCalculationWarnings = computed(() => {
    return localProduct.value.calculationWarnings?.length > 0
  })

  const isProductEditable = computed(() => {
    return productState.value !== PRODUCT_STATE.READONLY && !isProductReadOnlyHaX(productId.value)
  })

  const messages = computed(() => {
    if (props.personId) {
      return getProductMessages({
        categoryId: props.categoryId,
        hasCalculationWarnings: hasCalculationWarnings.value,
        modalRef: false,
        personId: props.personId,
        product: localProduct.value,
        productState: productState.value,
      })
    } else {
      return []
    }
  })

  const person = computed(() => {
    return getPerson(props.personId)
  })

  const pricePrefix = computed(() => {
    return localProduct.value?.showDiscountGroup && !localProduct.value.doctor ? t('product.asof') : ''
  })

  const productId = computed(() => {
    return props.product.productId
  })

  const productState = computed(() => {
    if (!person.value) return

    const partnerNumber = person.value.partnerNumber
    return state(partnerNumber, productId.value)
  })

  // METHODS
  async function add(__options) {
    if (__options.replace && !__options.forceAdd) {
      events$.emit(EVENT_PRODUCT.REPLACE_OPEN, {
        fromProductId: __options.productId,
        person: getPerson(props.personId),
        toProduct: props.product,
        source: props.source,
      })
    } else {
      try {
        loading$.start()
        const mode = isUpgrade(productId.value) ? 'ADD' : 'REPLACE'

        let basket = updateProductForPerson({ personId: props.personId, product: localProduct.value })
        basket = await addProductToOvpBasket(props.personId, productId.value, { basket, mode })
        basket = await basketStore.updateOvpBasket(basket)

        events$.emit(EVENT_PRODUCT.ADDED, {
          basket,
          categoryId: categoryId.value,
          personId: props.personId,
          productId: props.product.productId,
          source: props.source,
        })

        localProduct.value = props.product

        isDirty.value = false
        loading$.end()
      } catch {
        loading$.failed()
      }
    }
  }

  async function apply() {
    loading$.start()

    try {
      const basket = updateProductForPerson({ personId: props.personId, product: localProduct.value })

      await basketStore.updateOvpBasket(basket)

      isDirty.value = false

      loading$.end()
    } catch (error) {
      console.error(error)
      loading$.failed()
    }
  }

  async function remove() {
    loading$.start()

    try {
      const __basket = await removeProductFromPerson(props.personId, productId.value)

      events$.emit(EVENT_PRODUCT.REMOVED, {
        basket: __basket,
        categoryId: categoryId.value,
        personId: props.personId,
        productId: productId.value,
        source: props.source,
      })

      localProduct.value = props.product

      isDirty.value = false
      loading$.end()
    } catch {
      loading$.failed()
    }
  }

  function searchMedicalOffice() {
    events$.emit(EVENT_MEDICAL_OFFICE_MODAL_OPEN, {
      source: props.source,
      personId: props.personId,
      product: localProduct.value,
      callback: async data => {
        loading$.start()

        try {
          await addProductWithDoctor({
            categoryId: props.categoryId,
            doctor: data,
            personId: props.personId,
            productId: props.product.productId,
            source: props.source,
          })

          loading$.end()
        } catch {
          loading$.failed()
        }
      },
    })
  }

  async function selectOption(option) {
    loading$.start()

    try {
      const updatedProduct = await getSimulatedProductSelection({
        product: cloneDeep(localProduct.value),
        personId: props.personId,
        option,
      })

      ReactivityUtil.reAssign(localProduct.value, updatedProduct)
      if (localProduct.value.selected) {
        isDirty.value = true
      }
      loading$.end()
    } catch (error) {
      console.error(error)
      loading$.failed()
    }
  }

  // WATCHERS
  watch(
    () => props.product,
    value => {
      localProduct.value = value
    },
    { deep: true }
  )

  watchEffect(async () => {
    // has person, no selected product, an existing customer and basket is dynamic
    if (props.personId && !props.product.selected && basketStore.basket.existingCustomer && basketStore.basket.basketOfferEditScope === BASKET_OFFER_EDIT_SCOPE.ALL) {
      isReady.value = false

      try {
        // make sure product structure is ready for product widget
        await productStructureStore.ready()

        const _product = await getSimulatedProductSelection({
          productId: productId.value,
          personId: props.personId,
        })

        ReactivityUtil.reAssign(localProduct.value, Object.assign(
          {},
          productStructureStore.products.value[productId.value],
          _product,
        ))

        isReady.value = true
      } catch {
        // don't do a thing
      }
      isReady.value = true
    }
  })
</script>

<style scoped>
  .card {
    position: relative;
    display: grid;
    grid-row: 1 / -1;
    grid-template-columns: 1fr;
    grid-template-rows: subgrid;

    padding: var(--fixed-spacing-fix-06);
    border: 1px solid var(--outline-variant);
    border-radius: var(--radius-border-radius-03);

    &--selected {
      background-color: var(--surface-ext-b-low);
      border-color: var(--surface-ext-b-low);
    }

    &--unavailable {
      color: var(--on-surface-low);
    }

    .card__designation,
    .card__description {
      margin: var(--fixed-spacing-fix-04) 0 0;
    }

    .card__pricing:deep(.pricing):not(:empty) {
      margin: var(--fixed-spacing-fix-02) 0 0;
    }

    .card__criteria:not(:empty),
    .card__price,
    .card__action {
      margin: var(--dotcom-responsive-spacing-res-xxs) 0 0;
    }

    .card__message:not(:empty),
    .card__message .message + .message {
      margin: var(--fixed-spacing-fix-04) 0 0;
    }

    .card__action:deep(.button) {
      width: 100%;
    }

    .card__loader {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;

      display: grid;
      text-align: center;
      align-items: center;
      background-color: rgba(255, 255, 255, 0.64);

      .icon {
        align-self: center;
        justify-self: center;
        font-size: 24px;
      }
    }
  }
</style>
